import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLORS = {
  'Funding': 'bg-blue-100 text-blue-700',
  'Principal Distribution': 'bg-green-100 text-green-700',
  'Fee Collection': 'bg-orange-100 text-orange-700',
  'Fee Credit Applied': 'bg-purple-100 text-purple-700',
};

const formatDate = (d) => {
  if (!d) return '-';
  try {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d).split('T')[0];
  }
};

export default function SyndicationLedger({ embedded = false, syndicatorId = '' }) {
  // 11/03 (Josh: consolidate): rendered as a tab of the syndicator detail page with the syndicator fixed.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [syndicatorSearch, setSyndicatorSearch] = useState('');
  const [dealSearch, setDealSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');

  // Syndicator/Deal lists for autocomplete
  const [syndicators, setSyndicators] = useState([]);
  const [selectedSyndicator, setSelectedSyndicator] = useState(syndicatorId || '');

  const fetchLedger = useCallback(async () => {
    // 11/03 (Josh: "takes really long to load … didnt even load at all after 2 minutes"): the whole-book call
    // walked every position (154 s on Railway). The ledger is per syndicator — nothing loads until one is picked.
    if (!selectedSyndicator) { setEntries([]); setTotalCount(0); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSyndicator) params.append('syndicator_id', selectedSyndicator);
      if (eventFilter) params.append('event_type', eventFilter);

      const res = await axios.get(`${API}/syndications/capital-ledger?${params}`, {
        headers: { ...getAuthHeaders(), 'X-Tenant': 'live' },
      });
      const data = res.data;
      // Backend returns a raw array; guard against Array.prototype.entries being picked up as a truthy updater
      setEntries(Array.isArray(data) ? data : (Array.isArray(data?.entries) ? data.entries : []));
      setTotalCount(data?.total_count || (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load syndication ledger');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedSyndicator, eventFilter]);

  // Fetch syndicators list for filter
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/syndications/capital-dashboard`, {
          headers: { ...getAuthHeaders(), 'X-Tenant': 'live' },
        });
        const syns = res.data.map((d) => ({ id: d.syndicator_id, name: d.syndicator_name }));
        setSyndicators(syns);
      } catch (e) { console.error('Failed to load syndicators:', e); }
    })();
  }, [getAuthHeaders]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  // Client-side filters for syndicator name and deal name
  const filtered = entries.filter((e) => {
    if (syndicatorSearch && !e.syndicator_name?.toLowerCase().includes(syndicatorSearch.toLowerCase())) return false;
    if (dealSearch && !e.deal_name?.toLowerCase().includes(dealSearch.toLowerCase())) return false;
    return true;
  });

  return (
    <Wrap>
      <div className="space-y-6" data-testid="capital-ledger-page">
        {!embedded && <div>
          <h1 className="text-2xl font-bold text-slate-900">Syndication Ledger</h1>
          <p className="text-sm text-slate-500 mt-1">Append-only transaction log with running balances</p>
        </div>}

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          {!syndicatorId && <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Syndicator</label>
            <select
              value={selectedSyndicator}
              onChange={(e) => setSelectedSyndicator(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[180px]"
              data-testid="ledger-syndicator-filter"
            >
              <option value="">All Syndicators</option>
              {syndicators.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>}
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Event Type</label>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[180px]"
              data-testid="ledger-event-filter"
            >
              <option value="">All Events</option>
              <option value="Funding">Funding</option>
              <option value="Principal Distribution">Principal Distribution</option>
              <option value="Fee Collection">Fee Collection</option>
              <option value="Fee Credit Applied">Fee Credit Applied</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Search Deal</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Deal name..."
                value={dealSearch}
                onChange={(e) => setDealSearch(e.target.value)}
                className="pl-9 min-w-[180px]"
                data-testid="ledger-deal-search"
              />
            </div>
          </div>
        </div>

        {/* Ledger Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500">{selectedSyndicator ? 'No ledger entries found' : 'Pick a syndicator above to build their ledger.'}</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Syndicator</TableHead>
                      <TableHead>Deal</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Running Principal</TableHead>
                      <TableHead className="text-right">Running Adj. Capital</TableHead>
                      <TableHead className="text-right">Running Net Owed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((entry, idx) => (
                      <TableRow key={idx} data-testid={`ledger-row-${idx}`}>
                        <TableCell className="whitespace-nowrap text-sm">{formatDate(entry.date)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${EVENT_COLORS[entry.event_type] || 'bg-gray-100 text-gray-700'}`}>
                            {entry.event_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{entry.syndicator_name}</TableCell>
                        <TableCell className="text-sm">{entry.deal_name}</TableCell>
                        <TableCell className={`text-right text-sm font-medium ${entry.amount < 0 ? 'text-red-600' : ''}`}>
                          {entry.amount < 0 ? `-${formatCurrency(Math.abs(entry.amount))}` : formatCurrency(entry.amount)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[250px] truncate">{entry.description}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(entry.running_principal_returned)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(entry.running_adjusted_capital)}</TableCell>
                        <TableCell className={`text-right text-sm font-semibold ${entry.running_net_owed > 0 ? 'text-amber-600' : ''}`}>
                          {formatCurrency(entry.running_net_owed)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400">
          Showing {filtered.length} of {totalCount} entries{totalCount > 500 && !selectedSyndicator ? '. Use filters to narrow results.' : ''}. Ledger is read-only and derived from deal transactions.
        </p>
      </div>
    </Wrap>
  );
}
